<script>
  import { _ } from '../lib/i18n.js';
  import {
    X, ShieldCheck, AlertTriangle, AlertCircle, MinusCircle,
    Calendar, Cpu, FileCheck, Layers, ScrollText, Server, Package, ArrowRight,
    TrendingUp, TrendingDown, CheckCircle2,
  } from 'lucide-svelte';
  import ScoreGauge from './compliance/ScoreGauge.svelte';
  import MetricsGrid from './compliance/MetricsGrid.svelte';
  import FindingsTable from './compliance/FindingsTable.svelte';
  import {
    overallColors, sectionColors, severityColors, checklistColors,
    severityOrder, fallbackBadge,
  } from '../lib/complianceMaps.js';
  import { flattenFindings, collectHosts, isAggregateFinding } from '../lib/complianceMetrics.js';

  // The full API response: envelope fields + `report` (the structured payload).
  export let report = null;
  export let onClose = () => {};

  $: payload = report?.report ?? null;
  // Trend is driven by summary.delta (lifecycle), stamped by the backend.
  $: delta = payload?.summary?.delta ?? null;
  $: findingCounts = payload?.summary?.findingCounts ?? null;
  $: scope = payload?.scope ?? null;

  $: allFindings = flattenFindings(payload?.sections);
  $: realFindings = allFindings.filter((f) => !isAggregateFinding(f));
  $: hosts = collectHosts(allFindings);
  $: hasFindings = realFindings.length > 0;
  $: hasEvidence = !!(payload?.portfolioChecklist?.length || payload?.sbom);

  function pretty(value) {
    return String(value ?? '').replace(/[-_]/g, ' ');
  }

  // A resolved-finding key is namespaced "<section.key>::<finding.id>" — show
  // the section pretty-printed and the id verbatim (CVE ids etc. stay intact).
  function prettyFindingKey(key) {
    const [section, ...rest] = String(key ?? '').split('::');
    const id = rest.join('::');
    return id ? `${pretty(section)} · ${id}` : pretty(key);
  }

  function formatDate(value) {
    if (!value) return '—';
    // Date-only strings (YYYY-MM-DD — period dates, lifecycle dates) parse as UTC
    // midnight via `new Date`, rendering one calendar day early in a UTC+ zone
    // (Europe/Rome). Build them as local so the shown day is honest.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    return d.toLocaleDateString('it-IT', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  }

  function sectionFindingCount(section) {
    return (section.findings || []).filter((f) => !isAggregateFinding(f)).length;
  }
  const scopeModeLabel = (mode) => (['all', 'host', 'app'].includes(mode) ? $_('complianceReports.scope.' + mode) : pretty(mode));

  // ---- tabs ----
  $: tabs = [
    { id: 'overview', label: $_('complianceReports.tabs.overview'), icon: ShieldCheck },
    ...(hasFindings ? [{ id: 'findings', label: $_('complianceReports.tabs.findings'), icon: AlertTriangle, count: realFindings.length }] : []),
    { id: 'sections', label: $_('complianceReports.tabs.sections'), icon: Layers },
    ...(hasEvidence ? [{ id: 'evidence', label: $_('complianceReports.tabs.evidence'), icon: ScrollText }] : []),
  ];

  let activeTab = 'overview';
  let findingsIntent = null;
  let lastReportKey = null;
  // On opening a new report: jump host/all reports that carry findings straight
  // to the readable Findings table; everything else lands on the calm Overview.
  $: if (report) {
    const key = report.reportId ?? report.id ?? '';
    if (key !== lastReportKey) {
      lastReportKey = key;
      const mode = payload?.scope?.mode;
      activeTab = (mode === 'host' || mode === 'all') && realFindings.length ? 'findings' : 'overview';
      findingsIntent = null;
    }
  }

  function goToFindings(intent) {
    findingsIntent = { ...intent, _t: (findingsIntent?._t ?? 0) + 1 };
    activeTab = 'findings';
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') onClose();
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if report}
  <div class="modal-backdrop fade-in" on:click={onClose} role="presentation">
    <div
      class="modal-content max-w-5xl w-full max-h-[90vh] flex flex-col scale-in"
      on:click|stopPropagation
      role="dialog"
      aria-modal="true"
    >
      <!-- Header -->
      <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700 flex items-start justify-between flex-shrink-0">
        <div class="flex items-center space-x-3">
          <div class="p-2.5 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl shadow">
            <ShieldCheck size={24} class="text-white" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">
              {report.customerName || report.customerId} — {report.reportId}
            </h2>
            <p class="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
              <Calendar size={12} />
              <span>{formatDate(report.periodStart)} → {formatDate(report.periodEnd)}</span>
              <span>·</span>
              <span>{$_('complianceReports.generatedAt')}: {formatDateTime(report.generatedAt)}</span>
            </p>
          </div>
        </div>
        <button class="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg" on:click={onClose} aria-label={$_('common.close')}>
          <X size={20} />
        </button>
      </div>

      <!-- Tabs -->
      <div class="px-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <nav class="flex gap-6 overflow-x-auto">
          {#each tabs as tab}
            <button
              class="py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap transition-colors {activeTab === tab.id ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
              on:click={() => (activeTab = tab.id)}
            >
              <svelte:component this={tab.icon} size={16} />
              <span>{tab.label}</span>
              {#if tab.count}
                <span class="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{tab.count}</span>
              {/if}
            </button>
          {/each}
        </nav>
      </div>

      <!-- Body -->
      <div class="px-6 py-5 overflow-y-auto flex-1">
        {#if !payload}
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('complianceReports.noReports')}</p>

        {:else if activeTab === 'overview'}
          <div class="space-y-6">
            <!-- Hero: score gauge + status + severity rollup -->
            <div class="flex flex-wrap items-center gap-5">
              <ScoreGauge score={report.riskScore} />
              <div class="space-y-2">
                <span class="inline-block px-3 py-1.5 text-sm font-bold border-2 rounded-lg {overallColors[report.overallStatus] || fallbackBadge}">
                  {pretty(report.overallStatus)}
                </span>
                {#if findingCounts && severityOrder.some((s) => findingCounts[s])}
                  <div class="flex flex-wrap gap-1.5">
                    {#each severityOrder as sev}
                      {#if findingCounts[sev]}
                        <button
                          type="button"
                          class="px-2 py-0.5 text-xs font-bold border rounded hover:opacity-80 {severityColors[sev] || fallbackBadge}"
                          on:click={() => goToFindings({ severity: [sev] })}
                        >
                          {findingCounts[sev]} {$_('complianceReports.severity.' + sev)}
                        </button>
                      {/if}
                    {/each}
                  </div>
                {:else}
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold border rounded {checklistColors.provided}">
                    <CheckCircle2 size={13} /> {$_('complianceReports.noOpenFindings')}
                  </span>
                {/if}
              </div>
              <span class="text-xs text-gray-400 ml-auto self-start">schema v{report.schemaVersion}</span>
            </div>

            <!-- Summary & trend: lifecycle delta (v1.2.0) -->
            {#if payload.summary}
              <div class="card p-4 space-y-3">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                  <TrendingUp size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.summaryTrend')}</span>
                </h3>

                <!-- Trend is only meaningful against a prior period of the same stream.
                     The backend always stamps summary.delta (zeros are honest), so a
                     first-in-stream report has a delta but NO previousReportId — show the
                     neutral baseline note rather than a fabricated 0/0 "improvement". -->
                {#if delta?.previousReportId}
                  <div class="flex flex-wrap items-center gap-2.5 text-sm">
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border {checklistColors.provided}">
                      <CheckCircle2 size={14} />
                      <strong>{delta.resolvedFindings ?? 0}</strong> {$_('complianceReports.resolvedThisPeriod')}
                    </span>
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border {(delta.newCriticalFindings ?? 0) > 0 ? severityColors.critical : fallbackBadge}">
                      <AlertCircle size={14} />
                      <strong>{delta.newCriticalFindings ?? 0}</strong> {$_('complianceReports.newCritical')}
                    </span>
                    {#if delta.riskScoreChange !== null && delta.riskScoreChange !== undefined}
                      <span class="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                        <!-- riskScore is 0=worst..100=best, so a HIGHER score is better:
                             +Δ improved (green up), −Δ worsened (red down), 0 neutral. -->
                        {#if delta.riskScoreChange > 0}
                          <TrendingUp size={14} class="text-emerald-500" />
                        {:else if delta.riskScoreChange < 0}
                          <TrendingDown size={14} class="text-red-500" />
                        {:else}
                          <MinusCircle size={14} class="text-gray-400" />
                        {/if}
                        {$_('complianceReports.riskScoreChange')}: <strong>{delta.riskScoreChange > 0 ? '+' : ''}{delta.riskScoreChange}</strong>
                      </span>
                    {/if}
                    <span class="text-xs text-gray-400">{$_('complianceReports.vsPrevious')} {delta.previousReportId}</span>
                  </div>

                  {#if delta.resolvedFindingIds?.length}
                    <details class="text-sm">
                      <summary class="cursor-pointer text-gray-600 dark:text-gray-300 font-medium select-none">
                        {$_('complianceReports.resolvedFindings')} ({delta.resolvedFindingIds.length})
                      </summary>
                      <ul class="mt-1.5 space-y-1">
                        {#each delta.resolvedFindingIds as fk}
                          <li class="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <CheckCircle2 size={12} class="text-emerald-500 shrink-0 mt-0.5" />
                            <span class="font-mono break-all">{prettyFindingKey(fk)}</span>
                          </li>
                        {/each}
                      </ul>
                    </details>
                  {/if}

                  <p class="text-xs text-gray-400 italic">{$_('complianceReports.resolvedThisPeriodHint')}</p>
                {:else}
                  <p class="text-xs text-gray-400">{$_('complianceReports.noTrend')}</p>
                {/if}
              </div>
            {/if}

            <!-- Scope -->
            {#if scope && (scope.mode || scope.streamKey || hosts.length)}
              <div class="card p-4 space-y-2">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                  <Server size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.scope.label')}</span>
                </h3>
                <div class="flex flex-wrap items-center gap-2 text-sm">
                  {#if scope.mode}
                    <span class="px-2 py-0.5 text-xs font-bold border rounded {fallbackBadge}">{scopeModeLabel(scope.mode)}</span>
                  {/if}
                  {#if scope.streamKey}
                    <span class="font-mono text-xs text-gray-500 dark:text-gray-400">{scope.streamKey}</span>
                  {/if}
                  {#if hosts.length}
                    <span class="text-xs text-gray-500 dark:text-gray-400">· {$_('complianceReports.hostsMonitored', { values: { count: hosts.length } })}</span>
                  {/if}
                </div>
                {#if hosts.length}
                  <div class="flex flex-wrap gap-1">
                    {#each hosts as h}
                      <span class="px-1.5 py-0.5 text-[11px] font-mono border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300">{h}</span>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}

            <!-- Regulatory framing (v1.1.0 envelope) -->
            {#if payload.scopeAssessment || payload.reportSubject || payload.regulatoryReporting || payload.supportPeriod}
              <div class="card p-4 space-y-3">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                  <FileCheck size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.regulatoryFraming')}</span>
                </h3>

                {#if payload.scopeAssessment}
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {#if payload.scopeAssessment.nis2}
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">NIS2:</span>
                        <span class="font-semibold ml-1">{pretty(payload.scopeAssessment.nis2.status)}</span>
                        {#if payload.scopeAssessment.nis2.rationale}
                          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{payload.scopeAssessment.nis2.rationale}</p>
                        {/if}
                      </div>
                    {/if}
                    {#if payload.scopeAssessment.cra}
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">CRA:</span>
                        <span class="font-semibold ml-1">{pretty(payload.scopeAssessment.cra.status)}</span>
                        {#if payload.scopeAssessment.cra.rationale}
                          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{payload.scopeAssessment.cra.rationale}</p>
                        {/if}
                      </div>
                    {/if}
                  </div>
                {/if}

                {#if payload.reportSubject?.relationship}
                  <p class="text-sm text-gray-600 dark:text-gray-300">{payload.reportSubject.relationship}</p>
                {/if}

                {#if payload.regulatoryReporting?.craArt14}
                  <div class="text-sm">
                    <span class="text-gray-500 dark:text-gray-400">{$_('complianceReports.craArt14')}:</span>
                    <span class="font-semibold ml-1">{pretty(payload.regulatoryReporting.craArt14.readiness)}</span>
                    {#if payload.regulatoryReporting.craArt14.inForceFrom}
                      <span class="text-xs text-gray-400 ml-1">({$_('complianceReports.inForceFrom')} {payload.regulatoryReporting.craArt14.inForceFrom})</span>
                    {/if}
                  </div>
                {/if}

                {#if payload.supportPeriod?.endDate}
                  <div class="text-sm">
                    <span class="text-gray-500 dark:text-gray-400">{$_('complianceReports.supportPeriod')}:</span>
                    <span class="font-semibold ml-1">{formatDate(payload.supportPeriod.endDate)}</span>
                    {#if payload.supportPeriod.basis}<span class="text-xs text-gray-400 ml-1">({payload.supportPeriod.basis})</span>{/if}
                  </div>
                {/if}
              </div>
            {/if}

            <!-- Shared responsibility -->
            {#if payload.sharedResponsibility?.areas?.length}
              <div class="card p-4">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2 mb-3">
                  <Layers size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.sharedResponsibility')}</span>
                </h3>
                <div class="space-y-2">
                  {#each payload.sharedResponsibility.areas as area}
                    <div class="flex items-start gap-2 text-sm">
                      <span class="px-2 py-0.5 text-xs font-bold border rounded {fallbackBadge}">{pretty(area.responsibility)}</span>
                      <div>
                        <span class="font-medium text-gray-800 dark:text-gray-200">{area.area}</span>
                        {#if area.detail}<p class="text-xs text-gray-500 dark:text-gray-400">{area.detail}</p>{/if}
                      </div>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if payload.generator}
              <p class="text-xs text-gray-400 flex items-center gap-1">
                <Cpu size={12} /> {payload.generator.name} v{payload.generator.version}
              </p>
            {/if}

            {#if payload.disclaimer}
              <p class="text-xs text-gray-400 italic border-t border-gray-200/60 dark:border-gray-700 pt-3">{payload.disclaimer}</p>
            {/if}
          </div>

        {:else if activeTab === 'findings'}
          <FindingsTable
            findings={allFindings}
            {delta}
            scopeMode={scope?.mode}
            {hosts}
            intent={findingsIntent}
            reportId={report.reportId}
          />

        {:else if activeTab === 'sections'}
          <div class="space-y-3">
            {#each (payload.sections || []) as section}
              <details class="card p-0 overflow-hidden" open={section.status === 'fail' || section.status === 'warn'}>
                <summary class="px-4 py-3 cursor-pointer select-none flex items-center justify-between gap-3 list-none">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-semibold text-gray-900 dark:text-white">{section.title}</span>
                    {#if section.compliance?.length}
                      <span class="flex flex-wrap gap-1">
                        {#each section.compliance as c}
                          <span class="px-1.5 py-0.5 text-[10px] border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 rounded">{c.framework} {c.reference}</span>
                        {/each}
                      </span>
                    {/if}
                  </div>
                  <span class="px-2.5 py-1 text-xs font-bold border rounded shrink-0 {sectionColors[section.status] || fallbackBadge}">{pretty(section.status)}</span>
                </summary>
                <div class="px-4 pb-4 space-y-3 border-t border-gray-200/60 dark:border-gray-700 pt-3">
                  {#if section.narrative}
                    <p class="text-sm text-gray-600 dark:text-gray-300">{section.narrative}</p>
                  {/if}
                  <MetricsGrid metrics={section.metrics} sectionKey={section.key} onSeeFindings={(k) => goToFindings({ section: k })} />
                  {#if sectionFindingCount(section) > 0}
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      on:click={() => goToFindings({ section: section.key })}
                    >
                      {$_('complianceReports.viewFindings', { values: { count: sectionFindingCount(section) } })} <ArrowRight size={14} />
                    </button>
                  {/if}
                </div>
              </details>
            {/each}
          </div>

        {:else if activeTab === 'evidence'}
          <div class="space-y-4">
            {#if payload.portfolioChecklist?.length}
              <div class="card p-4">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2 mb-3">
                  <ScrollText size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.portfolioChecklist')}</span>
                </h3>
                <div class="space-y-1.5">
                  {#each payload.portfolioChecklist as item}
                    <div class="flex items-center justify-between gap-3 text-sm">
                      <div>
                        <span class="text-gray-700 dark:text-gray-200">{item.document}</span>
                        {#if item.reference}<span class="text-xs text-gray-400 ml-2">{$_('complianceReports.evidence.reference')}: {item.reference}</span>{/if}
                        {#if item.asOf}<span class="text-xs text-gray-400 ml-2">{$_('complianceReports.evidence.asOf')} {formatDate(item.asOf)}</span>{/if}
                      </div>
                      <span class="px-2 py-0.5 text-xs font-bold border rounded shrink-0 {checklistColors[item.status] || fallbackBadge}">{pretty(item.status)}</span>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if payload.sbom}
              <div class="card p-4">
                <h3 class="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2 mb-3">
                  <Package size={16} class="text-primary-500" />
                  <span>{$_('complianceReports.sbom.title')}</span>
                </h3>
                <div class="flex flex-wrap gap-2 text-sm">
                  {#if payload.sbom.format}
                    <span class="px-2 py-0.5 text-xs font-bold border rounded {fallbackBadge}">{payload.sbom.format}{#if payload.sbom.specVersion} {payload.sbom.specVersion}{/if}</span>
                  {/if}
                  {#if payload.sbom.componentCount != null}
                    <span class="text-xs text-gray-500 dark:text-gray-400">{$_('complianceReports.sbom.components', { values: { count: payload.sbom.componentCount } })}</span>
                  {/if}
                </div>
                {#if payload.sbom.ref}
                  <p class="text-xs text-gray-500 dark:text-gray-400 break-all mt-1 font-mono">{payload.sbom.ref}</p>
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Footer -->
      <div class="px-6 py-3 border-t border-gray-200/60 dark:border-gray-700 flex justify-end flex-shrink-0">
        <button class="btn btn-secondary text-sm" on:click={onClose}>{$_('common.close')}</button>
      </div>
    </div>
  </div>
{/if}
